import Head from 'next/head';
import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useAuth0User } from '@hooks/useAuth0User';
import { getFplId } from '@lib/page-utils/MyTeam';
import { useImportFromFpl } from '@hooks/my-team/useImportFromFpl';
import { useFplLinkGate } from '@hooks/useFplLinkGate';
import { useFplTeamId } from '@hooks/useFplTeamId';
import {
  FplLinkGate,
  FplLinkGateProfileError
} from '@components/my-team/FplLinkGate';
import { useAccessToken } from '@hooks/useAccessToken';
import WideLayout from '@components/layout/wide';
import {
  hasSufficientPrivileges,
  isPaidMember,
  isUserAuthenticated
} from '@lib/utils';
import { useIsAppView } from '@hooks/useIsAppView';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import { TeamImportModal } from '@components/my-team/TeamImportModal';
import Button from '@components/atoms/button';
import LoadingView from '@components/atoms/loading-view';
import { useLiveRank } from '@hooks/useLiveRank';
import {
  MEMBERSHIPS,
  PAID_MEMBERSHIP_LEVELS,
  UNAUTHENTICATED_FPL_ID,
  LIVE_RANK_MAINTENANCE_MESSAGE
} from '@lib/constants';
import Paywall from '@components/paywall';
import PaywallFixedBottomModal from '@components/paywall/PaywallFixedBottomModal';
import { useTriggerPopUpPaywall } from '@hooks/useTriggerPopUpPaywall';
import Maintenance from '@components/maintenace';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import FreeMemberModal from '@components/free-member-modal';
import { LiveRankTab } from '@components/live-rank-page/LiveRankTab';
import { LiveRankNotice } from '@components/live-rank-page/LiveRankNotice';
import { LivePitchSection } from '@components/live-rank-page/LivePitchSection';
import { TeamLiveProvider } from '@lib/live/team/context';
import { useHarnessSquad } from '@lib/live/harness/use-harness-squad';
import { useIsSimulationHarnessAvailable } from '@lib/live/harness/availability';
import { LiveSimulationHarness } from '@components/live-simulation-harness';
import { buildLiveTeamData } from '@lib/live/harness/squad';
import { getPageSeoHead } from '@lib/page-seo';

const MyTeamLivePointsPage = () => {
  const router = useRouter();
  const { pathname } = router;
  const queryClient = useQueryClient();
  const { fplTeamId, isLoading: profileIsLoading } = useFplTeamId();
  const { accessToken } = useAccessToken();
  const pageHead = getPageSeoHead(pathname);
  const { context } = useLeagueContext();
  const currentGameweek = context?.currentGameweek?.number ?? null;
  const nextGameweek = context?.nextGameweek?.number ?? null;
  const { user, isLoading: authIsLoading } = useAuth0User();
  const [isPaywallVisible, setIsPaywallVisible] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const hasPaidMembership = isPaidMember(user);
  const hasSufficientMembershipLevel = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.STANDARD_MONTHLY_ID].LEVEL
  );
  const [isFreeAccountModalOpen, setIsFreeAccountModalOpen] = useState(false);
  const hasHubAccount = isUserAuthenticated(user);
  const pointsPageIsClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.pointsPageIsClosed,
    false
  );

  const isInMaintenanceMode = usePostHogFeatureFlag(
    PostHogFeatureFlags.myTeamIsInMaintenance,
    false
  );

  const isAuthorisedToViewRestrictedPageContent =
    hasPaidMembership && hasSufficientMembershipLevel;

  const effectiveFplId = getFplId(fplTeamId);

  const openFreeAccountModal = () => {
    setIsFreeAccountModalOpen(true);
    setIsImportModalOpen(false);
  };

  const openImportModal = () => {
    if (!hasHubAccount) {
      openFreeAccountModal();
      return;
    }
    setIsImportModalOpen(true);
  };

  const importFromFpl = useImportFromFpl();
  const { status: linkGateStatus, refetchProfile } = useFplLinkGate();

  const handleImportTeam = async (fplTeamId: number) => {
    await importFromFpl.mutateAsync(fplTeamId);
    queryClient.invalidateQueries({
      queryKey: ['getLiveRankInfo', `live-rank-${fplTeamId}`]
    });
  };

  useTriggerPopUpPaywall({
    user,
    isAuthorisedToViewRestrictedPageContent,
    setShowModal: setIsPaywallVisible
  });

  useEffect(() => {
    // Wait for Auth0 to settle: while the session request is in flight the
    // user is undefined, which is indistinguishable from logged out and used
    // to flash this modal at logged-in members on a hard refresh.
    if (authIsLoading) {
      return;
    }

    const localStorageFplId =
      localStorage.getItem(UNAUTHENTICATED_FPL_ID) ?? '';

    if (!isUserAuthenticated(user) && localStorageFplId) {
      if (!hasHubAccount) {
        openFreeAccountModal();
      }
    }
  }, [user, authIsLoading]);

  const { liveRankData, lastFetchedTime, liveRankError } = useLiveRank({
    fplId: effectiveFplId,
    refetchInterval: 60000,
    queryKey: ['getLiveRankInfo', `live-rank-${effectiveFplId}`],
    currentGameweek,
    accessToken,
    isInMaintenanceMode
  });

  const { isAppView } = useIsAppView();

  // Non-production builds, for viewers the harness flag is released to. See
  // lib/live/harness. Availability is what opens the page, not the presence of
  // a squad: the panel is where a first squad gets built, so gating the page on
  // one would leave no way to reach it.
  const isHarnessAvailable = useIsSimulationHarnessAvailable();
  const { squad: harnessSquad, isResolved: isHarnessResolved } =
    useHarnessSquad();
  // Held stable across renders: the provider memoises picks and match ids off
  // this object, and rebuilding it every render would churn them on every tick
  // of a live match.
  const liveTeamOverride = useMemo(
    () => (harnessSquad ? buildLiveTeamData(harnessSquad) : null),
    [harnessSquad]
  );

  const hasValidFplId = effectiveFplId && effectiveFplId !== '0';

  // The page no longer blocks on live rank: the team pitch loads from its own
  // source and the rank widget degrades inline, so a rank outage can't hold
  // the whole page on a spinner (or take it down via the error boundary).
  //
  // The three season gates below all encode "the season has not started", via
  // a league context that reports no current gameweek until the sync flips
  // gameweek 1 to current at the deadline. That is precisely the window the
  // harness exists to work in, so an operator driving the page passes them.
  // Auth loading is a loading state too: every content block below is gated
  // on a settled auth state (!authIsLoading, or a link-gate status past
  // 'loading'), so rendering the main return while the session request is in
  // flight paints an empty shell and the footer jumps to the top. The
  // link-gate 'loading' term covers the window just after auth settles where
  // useProfile's isLoading is masked to false while the access token is
  // still resolving (see useFplLinkGate).
  const showLoadingComponent =
    authIsLoading ||
    (hasHubAccount && linkGateStatus === 'loading') ||
    (profileIsLoading && hasHubAccount) ||
    (!currentGameweek && !isHarnessAvailable);

  if (isInMaintenanceMode) {
    return (
      <>
        <Head>{pageHead}</Head>
        <WideLayout className="bg-white" content={false}>
          <div className="flex justify-center items-center">
            <Maintenance message={LIVE_RANK_MAINTENANCE_MESSAGE} />
          </div>
        </WideLayout>
      </>
    );
  }

  if (!isHarnessAvailable && (nextGameweek === 1 || pointsPageIsClosed)) {
    return (
      <>
        <Head>{pageHead}</Head>
        <EndOfSeasonHoldingMessage
          heading="Our Live feature"
          accentHeading="will be available after GW1 starts"
          status="Awaiting Gameweek 1"
          html="You'll be able to use our Live feature once Gameweek 1 is underway. In the meantime, use our AI to <a href='https://www.fantasyfootballhub.co.uk/my-team/pick'>get your team rated</a> and <a href='https://www.fantasyfootballhub.co.uk/my-team/transfers'>suggest changes</a>."
        />
      </>
    );
  }

  if (showLoadingComponent) {
    return (
      <div className="container mx-auto flex justify-center h-[calc(100vh-175px)] bg-white">
        <LoadingView
          headerText="Loading…"
          loadingText="Getting your live rank and points…"
          progressBarSeconds={8}
        />
      </div>
    );
  }

  return (
    <>
      <Head>{pageHead}</Head>
      <WideLayout className="bg-white" content={false}>
        {!authIsLoading && !hasHubAccount && (
          <div className="h-screen pt-12 p-4">
            <Paywall variant="embedded" title="🔒 Unlock exclusive content" />
          </div>
        )}
        {!authIsLoading && hasHubAccount && linkGateStatus === 'error' && (
          <FplLinkGateProfileError onRetry={refetchProfile} />
        )}
        {!authIsLoading && hasHubAccount && linkGateStatus === 'gate' && (
          <FplLinkGate
            onImported={(fplTeamId) =>
              queryClient.invalidateQueries({
                queryKey: ['getLiveRankInfo', `live-rank-${fplTeamId}`]
              })
            }
          />
        )}
        {/* During gameweek 1 itself the link gate passes (it only applies
            once GW1 has finished), so the original soft import nudge still
            covers unlinked users in that window.

            Suppressed for the harness, which is the other half of the same
            condition the pitch below is gated on. The harness fabricates its
            squad and needs no linked team, so asking an operator to import one
            is both wrong and, at py-32, enough dead space to push the tool
            itself below the fold. */}
        {!authIsLoading &&
          hasHubAccount &&
          linkGateStatus === 'pass' &&
          !hasValidFplId &&
          !isHarnessAvailable &&
          !isImportModalOpen && (
            <div className="py-32 flex flex-col justify-center items-center py">
              <div className="flex justify-center items-center px-10 mt-10 mb-6">
                <p
                  className="text-center font-sans"
                  data-cy="live-points-import-error"
                >
                  Please import your FPL team to view your live rank.
                </p>
              </div>
              <Button variant="primary" onClick={openImportModal}>
                Import
              </Button>
            </div>
          )}
        {/* The harness fabricates the squad, so it needs no linked FPL team.
            A fresh staging account would otherwise be held at the import
            prompt and never reach the pitch. */}
        {linkGateStatus === 'pass' && (hasValidFplId || isHarnessAvailable) && (
          <div
            className={`h-full bg-white py-4 px-2 md:p-8 pt-2 md:pt-0 ${
              isAppView ? 'mb-4' : '0'
            }`}
          >
            <div className="max-w-3xl mx-auto">
              {isHarnessAvailable && (
                <LiveSimulationHarness
                  accessToken={accessToken ?? ''}
                  defaultGameweek={currentGameweek ?? nextGameweek ?? 1}
                  activeSquad={harnessSquad}
                />
              )}
              {liveRankError === 'NOT_FOUND' && !harnessSquad ? (
                // FPL didn't recognise the saved team id: prompt a re-import
                // instead of attempting (and failing to load) the pitch.
                //
                // Not when a fabricated squad is driving, though. An unlinked
                // or unrecognised team is the ordinary state for the staging
                // account this exists to serve, and hiding the pitch behind a
                // re-import prompt would undo the link-gate bypass above.
                <LiveRankNotice
                  variant="notFound"
                  action={{ label: 'Re-import', onClick: openImportModal }}
                />
              ) : (
                <>
                  {liveRankData ? (
                    <LiveRankTab
                      lastFetchedTime={lastFetchedTime}
                      liveRankData={liveRankData}
                      currentGameweek={currentGameweek}
                    />
                  ) : liveRankError === 'UNAVAILABLE' ? (
                    <LiveRankNotice variant="unavailable" />
                  ) : null}
                  {/* Only mount the pitch once there is a team for it to
                      draw. The harness opens this page for staff accounts
                      that may have no linked FPL team, and with neither a
                      real team nor a fabricated one the provider settles on
                      IDLE, which LivePitchSection renders as its loading
                      skeleton. That skeleton would then sit there for ever,
                      claiming to be loading something that is never coming. */}
                  {(hasValidFplId || harnessSquad) && (
                    <TeamLiveProvider
                      fplId={effectiveFplId}
                      accessToken={accessToken}
                      isInMaintenanceMode={isInMaintenanceMode}
                      liveTeamOverride={liveTeamOverride}
                      isLiveTeamOverridePending={
                        isHarnessAvailable && !isHarnessResolved
                      }
                    >
                      <LivePitchSection />
                    </TeamLiveProvider>
                  )}
                </>
              )}
            </div>
            {(!hasHubAccount || !hasPaidMembership) && (
              <PaywallFixedBottomModal
                show={isPaywallVisible}
                handleClose={() => setIsPaywallVisible(false)}
              />
            )}
          </div>
        )}
        <TeamImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImportTeam}
          isImporting={importFromFpl.isLoading}
          title="Import your FPL team"
          description="Enter your FPL ID or search by team name."
        />
        <FreeMemberModal
          isOpen={isFreeAccountModalOpen}
          setIsOpen={setIsFreeAccountModalOpen}
          title="Create a free account to access our live rank feature"
          description="Once you've signed up, you'll have full access to the live rank feature"
        />
      </WideLayout>
    </>
  );
};

export default MyTeamLivePointsPage;
